// Транспорт Connect с подписью запросов.
//
// Подпись ставится на уровне `fetch`, а не в перехватчике сообщений, и это принципиально: сервер
// считает канон по байтам тела, как они пришли (§2 контракта). Перехватчик видит типизированное
// сообщение и был бы вынужден сериализовать его повторно — совпадение байт стало бы предположением,
// а не фактом. На уровне `fetch` мы подписываем ровно то, что уходит в сеть.

import { createConnectTransport } from "@connectrpc/connect-web";
import type { Transport } from "@connectrpc/connect";

import type { Clock, RandomSource } from "./platform.ts";
import { systemClock, webCryptoRandom } from "./platform.ts";
import type { DeviceSigner } from "./signing.ts";
import { newNonce, signRequest, signatureHeaders, timestampSeconds } from "./signing.ts";

/** Что ядро знает о текущей сессии в момент запроса; всё необязательно — до входа ничего этого нет. */
export interface AuthState {
  /** Токен сессии для `Authorization: Bearer`. */
  token?: string | undefined;
  /** Идентификатор устройства для `X-Yofis-Device`. */
  deviceId?: string | undefined;
  /** Ключ устройства; без него запросы уровня signed не подписать. */
  signer?: DeviceSigner | undefined;
}

/** Источник текущей сессии: транспорт читает её перед каждым запросом, а не запоминает при создании. */
export interface AuthSource {
  current(): AuthState;
}

/** Настройки транспорта. */
export interface TransportOptions {
  /** Адрес узла, например `https://company.ru`. */
  baseUrl: string;
  /** Откуда брать токен и ключ; без него транспорт годится только для публичных вызовов. */
  auth?: AuthSource;
  /** Значение заголовка `X-Yofis-Client`, например `yofis-web/0.1.0`. */
  client?: string;
  clock?: Clock;
  random?: RandomSource;
  /** Подмена сети — для тестов и для Electron. */
  fetch?: typeof fetch;
}

const anonymous: AuthSource = { current: () => ({}) };

/** Транспорт Connect к узлу Yofis: protobuf по проводу, подпись и токен проставляются сами. */
export function createYofisTransport(options: TransportOptions): Transport {
  const auth = options.auth ?? anonymous;
  const clock = options.clock ?? systemClock;
  const random = options.random ?? webCryptoRandom;
  const baseFetch = options.fetch ?? globalThis.fetch;

  const signing: typeof fetch = async (input, init) => {
    const request = new Request(input, init);
    const url = new URL(request.url);
    const state = auth.current();
    const headers = new Headers(request.headers);
    if (options.client !== undefined) {
      headers.set(signatureHeaders.client, options.client);
    }
    if (state.token !== undefined) {
      headers.set("Authorization", `Bearer ${state.token}`);
    }
    // Тело читаем из копии запроса: исходный поток остаётся нетронутым для отправки.
    const body = new Uint8Array(await request.clone().arrayBuffer());
    if (state.signer !== undefined && state.deviceId !== undefined) {
      const signed = await signRequest(state.signer, state.deviceId, {
        methodPath: url.pathname,
        timestamp: timestampSeconds(clock),
        nonce: newNonce(random),
        body,
      });
      for (const [name, value] of Object.entries(signed)) {
        headers.set(name, value);
      }
    }
    // Тело передаём уже прочитанным: повторно поток не отдать, а ретраев на этом уровне нет.
    const init2: RequestInit = {
      method: request.method,
      headers,
      ...(request.method === "GET" || request.method === "HEAD" ? {} : { body }),
      signal: request.signal,
      credentials: "omit",
      redirect: "error",
    };
    return baseFetch(url, init2);
  };

  return createConnectTransport({
    baseUrl: options.baseUrl,
    // Протокол на проводе — protobuf: канон подписи считается по тем же байтам на обеих сторонах.
    useBinaryFormat: true,
    fetch: signing,
  });
}
